/**
 * SharedFiltersProvider — Phase 5.0.
 * Owns the cross-module dashboard filter state (team_name, managers, assignees)
 * + its load status. Extracted from FintechContextProvider.
 *
 * The actual filter-calculation effect that reads from each dataset still lives
 * in the legacy FintechContextProvider during Phase 5; it will migrate to this
 * provider (or a hook that reads from domain providers) in Phase 6.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DashboardFilters } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';

export type FiltersStatus = 'pending' | 'loading' | 'finished' | 'error';

export interface SharedFiltersContextValue {
  dashboardFilterData: DashboardFilters;
  setDashboardFilterData: (filters: DashboardFilters) => void;

  dashboardFilterStatus: FiltersStatus;
  setDashboardFilterStatus: (status: FiltersStatus) => void;

  dashboardFilterDataCalculationStatus: FiltersStatus;
  setDashboardFilterDataCalculationStatus: (status: FiltersStatus) => void;
}

const INITIAL_FILTERS = {} as DashboardFilters;

const defaultValue: SharedFiltersContextValue = {
  dashboardFilterData: INITIAL_FILTERS,
  setDashboardFilterData: () => {},
  dashboardFilterStatus: 'loading',
  setDashboardFilterStatus: () => {},
  dashboardFilterDataCalculationStatus: 'loading',
  setDashboardFilterDataCalculationStatus: () => {}
};

const SharedFiltersContext = createContext<SharedFiltersContextValue>(defaultValue);

export const useSharedFilters = () => useContext(SharedFiltersContext);

interface Props {
  children: React.ReactNode;
}

export const SharedFiltersProvider = ({ children }: Props) => {
  const [dashboardFilterData, setDashboardFilterDataState] = useState<DashboardFilters>(INITIAL_FILTERS);
  const [dashboardFilterStatus, setDashboardFilterStatusState] = useState<FiltersStatus>('loading');
  const [dashboardFilterDataCalculationStatus, setDashboardFilterDataCalculationStatusState] = useState<FiltersStatus>('loading');

  const setDashboardFilterData = useCallback((f: DashboardFilters) => setDashboardFilterDataState(f), []);
  const setDashboardFilterStatus = useCallback((s: FiltersStatus) => setDashboardFilterStatusState(s), []);
  const setDashboardFilterDataCalculationStatus = useCallback((s: FiltersStatus) => setDashboardFilterDataCalculationStatusState(s), []);

  const value = useMemo(
    () => ({
      dashboardFilterData,
      setDashboardFilterData,
      dashboardFilterStatus,
      setDashboardFilterStatus,
      dashboardFilterDataCalculationStatus,
      setDashboardFilterDataCalculationStatus
    }),
    [
      dashboardFilterData,
      setDashboardFilterData,
      dashboardFilterStatus,
      setDashboardFilterStatus,
      dashboardFilterDataCalculationStatus,
      setDashboardFilterDataCalculationStatus
    ]
  );

  return <SharedFiltersContext.Provider value={value}>{children}</SharedFiltersContext.Provider>;
};
